import React, { useEffect, useRef, useState } from 'react';
import { UserPath } from './PathSelector';
import { SeniorPathContent } from './SeniorPathContent';
import { VolunteerPathContent } from './VolunteerPathContent';
import { ProfessionalPathContent } from './ProfessionalPathContent';

interface ProgressiveContentAreaProps {
    selectedPath: UserPath;
    onFormScroll: (pathType?: string) => void;
    className?: string;
}

export const ProgressiveContentArea: React.FC<ProgressiveContentAreaProps> = ({
    selectedPath,
    onFormScroll,
    className = ''
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    // Handle path selection changes
    useEffect(() => {
        if (selectedPath) {
            // First make the content exist in DOM
            setShouldRender(true);

            // Small delay to ensure DOM is ready, then expand and show
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, 50);

            return () => clearTimeout(timer);
        } else {
            // Hide content first, then remove from DOM
            setIsVisible(false);
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300); // Wait for collapse animation

            return () => clearTimeout(timer);
        }
    }, [selectedPath]);

    const handleGetStarted = () => {
        const pathTypeMap = {
            senior: 'GETTING_HELP',
            volunteer: 'VOLUNTEERING',
            professional: 'PROFESSIONAL_SERVICES'
        };

        const formType = selectedPath ? pathTypeMap[selectedPath] : undefined;
        onFormScroll(formType);
    };

    if (!shouldRender) {
        return null;
    }

    const renderPathContent = () => {
        switch (selectedPath) {
            case 'senior':
                return <SeniorPathContent onGetStarted={handleGetStarted} />;
            case 'volunteer':
                return <VolunteerPathContent onGetStarted={handleGetStarted} />;
            case 'professional':
                return <ProfessionalPathContent onGetStarted={handleGetStarted} />;
            default:
                return null;
        }
    };

    return (
        <div
            ref={contentRef}
            className={`progressive-content-area overflow-hidden transition-all duration-500 ease-in-out ${isVisible ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
                } ${className}`}
            style={{
                maxHeight: isVisible ? '10000px' : '0px' // Large enough to accommodate any content
            }}
        >
            <div className={`transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                {renderPathContent()}
            </div>
        </div>
    );
};
